// The written horoscope, set as the almanac's column: the headline in
// italic, the reading of the three signs as one sky, a hairline, then a
// short portrait per sign under its glyph, name and word, with the signs'
// words picked out wherever they stand in the text. Just written, it is set
// block by block; otherwise it is simply there. A placeholder's says so,
// with a way to write it again.
import { Fragment, type ReactNode } from 'react';
import type { Horoscope, Sign } from '../../systems/api/types';
import { SIGNS } from '../../systems/horoscope/horoscopeStore';
import { Button } from '../components/Button';
import { SIGN_LABELS, SignGlyph } from './signs';

interface Props {
  horoscope: Horoscope & { body: NonNullable<Horoscope['body']> };
  reveal: boolean;
  /** Write a placeholder's horoscope again (by the real writer once it is connected); tonight only. */
  onRewrite?: () => void;
}

export function HoroscopeText({ horoscope, reveal, onRewrite }: Props) {
  const { body, signs } = horoscope;
  const words = [...new Set(SIGNS.map((s) => signs[s].word))];
  // Each block in turn, when it is being set: its class plus the reveal, and its delay.
  let line = 0;
  const step = (className: string) =>
    reveal ? { className: `${className} reveal`, style: { animationDelay: `${line++ * 160}ms` } } : { className };
  return (
    <article className="horoscope-text">
      <h2 {...step('horoscope-headline')}>{markWords(body.headline, words)}</h2>
      <p {...step('horoscope-reading')}>{markWords(body.reading, words)}</p>
      <hr {...step('horoscope-rule')} />
      {SIGNS.map((sign: Sign) => {
        return (
          <section key={sign} {...step('horoscope-sign')}>
            <h3 className="horoscope-sign-head label-mono">
              <SignGlyph sign={sign} />
              {SIGN_LABELS[sign]} · {signs[sign].word}
            </h3>
            <p>{markWords(body[sign], words)}</p>
          </section>
        );
      })}
      {horoscope.placeholder && (
        <div {...step('horoscope-placeholder')}>
          <p className="quiet">Set from a template while the writer is being connected.</p>
          {onRewrite && (
            <Button variant="ghost" small onClick={onRewrite}>
              Write it again
            </Button>
          )}
        </div>
      )}
    </article>
  );
}

/** The signs' words wherever they stand in capitals in the text. */
function markWords(text: string, words: string[]): ReactNode {
  if (words.length === 0) return text;
  const pattern = new RegExp(`\\b(${words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})\\b`, 'g');
  const parts = text.split(pattern);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <span key={i} className="sign-word">
        {part}
      </span>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    ),
  );
}
